import React, { useEffect } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  TextField,
  Typography,
  Paper,
  IconButton,
  Avatar,
  Stack,
  Tooltip,
  alpha
} from "@mui/material";
import { Close, StorageRounded, Add, Delete, ListRounded, ShieldRounded, InfoOutlined } from "@mui/icons-material";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useMutation } from "react-query";
import { useSnackbar } from "notistack";

// Resource Servers Update/Create Component
function ResourceServersUpdate({
  handleModalClose,
  isNew,
  serverData,
  refetch
}) {
  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();

  // HOOK FORM
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    mode: "onChange",
    defaultValues: isNew ? { name: "", scopes: [{ name: "", description: "" }] } : {
      ...serverData,
      scopes: serverData?.scopes || []
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "scopes"
  });

  // MUTATIONS
  const { mutate: create, isLoading: isCreating } = useMutation((formData) =>
    axiosPrivate.post(`/resourceServers`, formData)
  );

  const { mutate: update, isLoading: isUpdating } = useMutation((formData) =>
    axiosPrivate.put(`/resourceServers/${serverData.id}`, formData)
  );

  const onSubmit = (formData) => {
    const action = isNew ? create : update;
    action(formData, {
      onSuccess: () => {
        enqueueSnackbar(`Resource Server ${isNew ? 'created' : 'updated'} successfully.`, { variant: "success" });
        handleModalClose();
        refetch();
      },
      onError: (err) => {
        const message = err?.response?.data?.error?.message || "Operation failed";
        enqueueSnackbar(message, { variant: "error" });
      }
    });
  };

  const actionLoading = isCreating || isUpdating;

  return (
    <Grid container sx={{ minHeight: "100vh", justifyContent: "center", alignItems: { xs: "flex-end", sm: "center" }, p: { xs: 0, sm: 2 } }}>
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: { xs: '100%', sm: '600px' },
          borderRadius: { xs: '24px 24px 0 0', sm: 6 },
          boxShadow: theme => theme.palette.mode === 'light'
            ? "0px 20px 60px rgba(0, 0, 0, 0.15)"
            : `0px 20px 60px ${alpha(theme.palette.common.black, 0.5)}`,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '95vh'
        }}
      >
        <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: 'secondary.main', borderRadius: 2 }}>
              <StorageRounded />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>{isNew ? "Register API Server" : "Update API Server"}</Typography>
          </Stack>
          <IconButton onClick={handleModalClose} size="small"><Close /></IconButton>
        </Box>

        <Box sx={{ p: { xs: 3, sm: 4 }, overflowY: 'auto', flex: 1 }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={4}>
              <FormSection title="Core Configuration" icon={<ShieldRounded fontSize="inherit" />}>
                <Box sx={{ mt: 2 }}>
                  <Controller
                    name="name"
                    control={control}
                    rules={{ required: "Server name is required" }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        label="Service Identifier / Name"
                        placeholder="e.g. Identity Management API"
                        error={!!errors.name}
                        helperText={errors.name?.message}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                      />
                    )}
                  />
                </Box>
              </FormSection>

              <FormSection
                title="Permission Scopes"
                icon={<ListRounded fontSize="inherit" />}
                hint="Define specific access levels for this Resource Server."
              >
                <Stack spacing={2} sx={{ mt: 2 }}>
                  {fields.map((field, index) => (
                    <Paper
                      key={field.id}
                      elevation={0}
                      sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}
                    >
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={4}>
                            <Controller
                              name={`scopes.${index}.name`}
                              control={control}
                              rules={{ required: "Required" }}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  size="small"
                                  label="Scope Name"
                                  placeholder="read:users"
                                  error={!!errors.scopes?.[index]?.name}
                                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5 }, bgcolor: 'background.paper' }}
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={8}>
                            <Controller
                              name={`scopes.${index}.description`}
                              control={control}
                              rules={{ required: "Required" }}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  size="small"
                                  label="Description"
                                  placeholder="Allows reading user profile data"
                                  error={!!errors.scopes?.[index]?.description}
                                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5 }, bgcolor: 'background.paper' }}
                                />
                              )}
                            />
                          </Grid>
                        </Grid>
                        <IconButton
                          onClick={() => remove(index)}
                          disabled={fields.length === 1}
                          color="error"
                          size="small"
                          sx={{ mt: 0.5 }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Paper>
                  ))}
                  <Button
                    startIcon={<Add />}
                    onClick={() => append({ name: "", description: "" })}
                    sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
                  >
                    Add New Scope
                  </Button>
                </Stack>
              </FormSection>

              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button fullWidth variant="outlined" color="inherit" onClick={handleModalClose} sx={{ borderRadius: 3, py: 1.2, fontWeight: 700, textTransform: 'none' }}>Cancel</Button>
                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={actionLoading}
                  sx={{
                    borderRadius: 3,
                    py: 1.2,
                    fontWeight: 700,
                    textTransform: 'none',
                    bgcolor: 'secondary.main',
                    '&:hover': { bgcolor: 'secondary.dark' },
                    boxShadow: theme => `0 8px 20px ${alpha(theme.palette.common.black, 0.2)}`
                  }}
                >
                  {actionLoading ? <CircularProgress size={24} color="inherit" /> : (isNew ? "Register" : "Update")}
                </Button>
              </Box>
            </Stack>
          </form>
        </Box>
      </Paper>
    </Grid>
  );
}

function FormSection({ title, icon, hint, children }) {
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box sx={{ color: 'primary.main', fontSize: 18, display: 'flex' }}>{icon}</Box>
        <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</Typography>
        {hint && (
          <Tooltip title={hint} arrow>
            <InfoOutlined sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
          </Tooltip>
        )}
      </Stack>
      {children}
    </Box>
  );
}

export default ResourceServersUpdate;